import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBookingStore } from '../stores/bookingStore';
import { Vehicle } from '../types';
import { formatPrice } from '../utils/dateUtils';
import VehicleReviews from '../components/vehicle/VehicleReviews';
import RangeCalendar from '../components/calendar/RangeCalendar';
import { apiClient } from '../api/client';
import Vehicle360Viewer from '../components/vehicle/Vehicle360Viewer';
import {
  localizeCarClass,
  localizeCompany,
  localizeFuel,
  localizeLocation,
  localizeOption,
  localizeVehicleModel,
} from '../utils/localization';

const VehicleDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [currentVehicle, setCurrentVehicle] = useState<Vehicle>(location.state?.vehicle as Vehicle);
  const { startDateTime, endDateTime, getTotalHours, getExactDuration, setStartDateTime, setEndDateTime, setSelectedVehicle } = useBookingStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [availableImages, setAvailableImages] = useState<string[]>([]);

  // Reset loading state when image index changes
  useEffect(() => {
    setImageLoading(true);
  }, [currentImageIndex]);
  const [has360Image, setHas360Image] = useState(false);
  const [image360Path, setImage360Path] = useState('');
  const [showDateModal, setShowDateModal] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [tempStartTime, setTempStartTime] = useState<string>('09:00');
  const [tempEndTime, setTempEndTime] = useState<string>('18:00');
  const [isVehicleAvailable, setIsVehicleAvailable] = useState(true);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const isEnglish = i18n.language.startsWith('en');
  const weekdays = t('vehicleList.weekdays', { returnObjects: true }) as string[];
  const carClassLabels: Record<string, string> = {
    경차: 'Compact',
    소형: 'Economy',
    준중형: 'Compact Sedan',
    중형: 'Midsize',
    대형: 'Full-size',
    수입: 'Imported',
    RV: 'RV',
    SUV: 'SUV',
  };
  const fuelLabels: Record<string, string> = {
    휘발유: 'Gasoline',
    LPG: 'LPG',
    경유: 'Diesel',
    전기: 'Electric',
    하이브리드: 'Hybrid',
  };
  const companyLabels: Record<string, string> = {
    현대: 'Hyundai',
    기아: 'Kia',
    테슬라: 'Tesla',
    제네시스: 'Genesis',
    쉐보레: 'Chevrolet',
    르노: 'Renault',
    쌍용: 'KG Mobility',
    KG모빌리티: 'KG Mobility',
  };
  const locationLabels: Record<string, string> = {
    본사: 'Main Office',
    서면: 'Seomyeon',
    가야: 'Gaya',
  };
  const modelLabels: Record<string, string> = {
    베뉴: 'Venue',
    '제네시스 G80': 'Genesis G80',
    G80: 'Genesis G80',
    '테슬라Model Y': 'Tesla Model Y',
    '테슬라 Model Y': 'Tesla Model Y',
    'Model Y': 'Model Y',
    '볼트 EV': 'Bolt EV',
    '볼트(EV)': 'Bolt EV',
    아반떼: 'Avante',
    '아반떼 AD': 'Avante AD',
    '아반떼 CN7': 'Avante CN7',
    '아반떼 CN7 FL': 'Avante CN7 FL',
    K5: 'K5',
    '3세대 K5': '3rd Gen K5',
    소나타: 'Sonata',
    '소나타 DN8': 'Sonata DN8',
    쏘나타: 'Sonata',
    '쏘나타 디 엣지': 'Sonata The Edge',
    그랜저: 'Grandeur',
    그랜져: 'Grandeur',
    '그랜저 GN7': 'Grandeur GN7',
    '그랜져 GN7': 'Grandeur GN7',
    캐스퍼: 'Casper',
    레이: 'Ray',
    토레스: 'Torres',
    스포티지: 'Sportage',
    카니발: 'Carnival',
    '4세대 카니발': '4th Gen Carnival',
    '4세대카니발': '4th Gen Carnival',
    '카니발 리무진 7인승': 'Carnival Limousine 7-Seater',
    '카니발 리무진 4세대 7인승': '4th Gen Carnival Limousine 7-Seater',
    '4세대 카니발 리무진': '4th Gen Carnival Limousine',
    '4세대카니발 리무진': '4th Gen Carnival Limousine',
    '4세대카니발리무진': '4th Gen Carnival Limousine',
    '카니발 9인승': 'Carnival 9-Seater',
    'All New 카니발 9인승': 'All New Carnival 9-Seater',
    'All New 카니발 4세대 9인승': 'All New 4th Gen Carnival 9-Seater',
    '카니발 리무진': 'Carnival Limousine',
    'All New 카니발 리무진': 'All New Carnival Limousine',
    그랜드스타렉스: 'Grand Starex',
    스타리아: 'Staria',
    '스타리아 9인승': 'Staria 9-Seater',
    '스타리아 11인승': 'Staria 11-Seater',
    스타렉스: 'Starex',
    쏠라티: 'Solati',
    셀토스: 'Seltos',
    쏘렌토: 'Sorento',
    '4세대 쏘렌토 FL': '4th Gen Sorento FL',
    '소렌토MQ4 FL': 'Sorento MQ4 FL',
    싼타페: 'Santa Fe',
    투싼: 'Tucson',
    팰리세이드: 'Palisade',
    '디 올뉴 펠리세이드': 'The All-New Palisade',
  };
  const optionLabels: Record<string, string> = {
    네비게이션: 'Navigation',
    네비: 'Navigation',
    블랙박스: 'Dashcam',
    하이패스: 'Hi-pass',
    블루투스: 'Bluetooth',
    열선시트: 'Heated Seats',
    '휴대폰 무선 충전': 'Wireless Phone Charging',
    '무선 충전': 'Wireless Charging',
    코일메트: 'Coil Mats',
    코일매트: 'Coil Mats',
  };
  const getModelLabel = (modelName?: string) => {
    return localizeVehicleModel(modelName, i18n.language);
  };
  const getCarClassLabel = (carClass?: string) => {
    return localizeCarClass(carClass, i18n.language);
  };
  const getFuelLabel = (fuel?: string) => {
    return localizeFuel(fuel, i18n.language);
  };
  const getCompanyLabel = (company?: string) => {
    return localizeCompany(company, i18n.language);
  };
  const getLocationLabel = (locationName?: string) => {
    return localizeLocation(locationName, i18n.language);
  };
  const getOptionLabel = (option?: string) => {
    return localizeOption(option, i18n.language);
  };
  const formatDuration = () => {
    const parts = [];
    if (days > 0) parts.push(`${days}${t('vehicleList.day')}`);
    if (remainingHours > 0) parts.push(`${remainingHours}${t('vehicleList.hour')}`);
    if (remainingMinutes > 0) parts.push(`${remainingMinutes}${t('vehicleList.minute')}`);
    return parts.join(' ');
  };
  const formatYear = () => {
    if (currentVehicle.year) {
      return isEnglish ? `${currentVehicle.year}` : `${currentVehicle.year}년`;
    }
    if ((currentVehicle as any).caryear && (currentVehicle as any).carmonth) {
      return isEnglish
        ? `${(currentVehicle as any).caryear}.${(currentVehicle as any).carmonth}`
        : `${(currentVehicle as any).caryear}년 ${(currentVehicle as any).carmonth}월`;
    }
    return '';
  };

  // Update vehicle when navigating back from checkout
  useEffect(() => {
    if (location.state?.vehicle) {
      setCurrentVehicle(location.state.vehicle as Vehicle);
      setCurrentImageIndex(0);
    }
  }, [location.pathname]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize temp date/time when modal opens
  useEffect(() => {
    if (showDateModal) {
      if (startDateTime) {
        setTempStartDate(startDateTime);
        const startHour = startDateTime.getHours().toString().padStart(2, '0');
        const startMin = startDateTime.getMinutes().toString().padStart(2, '0');
        setTempStartTime(`${startHour}:${startMin}`);
      }
      if (endDateTime) {
        setTempEndDate(endDateTime);
        const endHour = endDateTime.getHours().toString().padStart(2, '0');
        const endMin = endDateTime.getMinutes().toString().padStart(2, '0');
        setTempEndTime(`${endHour}:${endMin}`);
      }
    }
  }, [showDateModal, startDateTime, endDateTime]);

  const handleSaveDateTimeModal = async () => {
    if (tempStartDate && tempEndDate) {
      const [startHour, startMin] = tempStartTime.split(':').map(Number);
      const [endHour, endMin] = tempEndTime.split(':').map(Number);

      let newStart = new Date(tempStartDate);
      newStart.setHours(startHour, startMin, 0, 0);
      let newEnd = new Date(tempEndDate);
      newEnd.setHours(endHour, endMin, 0, 0);

      // Validate minimum 5 hours rental
      const diffInMinutes = (newEnd.getTime() - newStart.getTime()) / (1000 * 60);
      const MIN_RENTAL_MINUTES = 5 * 60;

      if (diffInMinutes < MIN_RENTAL_MINUTES) {
        // Auto-adjust end time to maintain 5-hour minimum
        const adjustedEndTime = new Date(newStart.getTime() + MIN_RENTAL_MINUTES * 60 * 1000);
        const adjustedEndHour = adjustedEndTime.getHours();
        const adjustedEndMin = adjustedEndTime.getMinutes();

        // If adjusted end time exceeds 22:00 on the same day, move to next day at 08:00
        const isSameDay = tempStartDate.toDateString() === tempEndDate.toDateString();
        if (isSameDay && (adjustedEndHour > 22 || (adjustedEndHour === 22 && adjustedEndMin > 0))) {
          const nextDay = new Date(tempEndDate);
          nextDay.setDate(nextDay.getDate() + 1);
          newEnd = new Date(
            nextDay.getFullYear(),
            nextDay.getMonth(),
            nextDay.getDate(),
            8,
            0,
            0,
            0
          );
          alert(t('vehicleList.returnTimeAdjustedNextDay'));
        } else {
          newEnd = adjustedEndTime;
          alert(t('vehicleList.minimumRentalAdjusted'));
        }
      }

      setCheckingAvailability(true);

      try {
        // Format dates for API
        const formatDateTime = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hour = String(date.getHours()).padStart(2, '0');
          return `${year}-${month}-${day} ${hour}`;
        };

        const requestData = {
          start_date: formatDateTime(newStart),
          end_date: formatDateTime(newEnd),
          car_name: currentVehicle.model_name,
        };

        console.log('Checking vehicle availability:', requestData);

        const response = await apiClient.post('/vehicles/search', requestData);

        if (response.data.code === '0' && Array.isArray(response.data.data)) {
          const availableVehicles = response.data.data.filter(
            (item: any) => (item.carname || item.model_name) === currentVehicle.model_name
          );

          if (availableVehicles.length > 0) {
            // Vehicle is available - update dates and vehicle info
            setStartDateTime(newStart);
            setEndDateTime(newEnd);
            setIsVehicleAvailable(true);

            // Update vehicle with first available vehicle of same model
            const firstAvailable = availableVehicles[0];
            const updatedVehicle = {
              ...currentVehicle,
              id: firstAvailable.idx,
              count: availableVehicles.length,
              ids: availableVehicles.map((v: any) => v.idx),
              license_plate: firstAvailable.carnum || firstAvailable.license_plate || currentVehicle.license_plate,
              company: firstAvailable.company || (currentVehicle as any).company,
              seating_capacity: parseInt(firstAvailable.people) || currentVehicle.seating_capacity || 5,
              capacity: parseInt(firstAvailable.people) || currentVehicle.capacity || 5,
              weekday_rate: parseInt(firstAvailable.daymoney) || currentVehicle.weekday_rate || 50000,
              weekend_rate: parseInt(firstAvailable.weekmoney) || currentVehicle.weekend_rate || 70000,
              five_hour_rate: parseInt(firstAvailable.fivemoney) || currentVehicle.five_hour_rate || 0,
              CarSomoName: firstAvailable.CarSomoName || firstAvailable.carsomoname || (currentVehicle as any).CarSomoName,
            };
            setCurrentVehicle(updatedVehicle);

            setShowDateModal(false);
          } else {
            // No vehicles of this model available for selected dates
            setIsVehicleAvailable(false);
            setStartDateTime(newStart);
            setEndDateTime(newEnd);
            setShowDateModal(false);
            alert(t('vehicleDetail.unavailableAlert'));
          }
        } else {
          setIsVehicleAvailable(false);
          alert(t('vehicleDetail.availabilityUnknown'));
        }
      } catch (error) {
        console.error('Failed to check vehicle availability:', error);
        setIsVehicleAvailable(false);
        alert(t('vehicleDetail.availabilityError'));
      } finally {
        setCheckingAvailability(false);
      }
    }
  };

  const handleRangeSelect = (start: Date | null, end: Date | null) => {
    setTempStartDate(start);
    setTempEndDate(end);
  };

  // Check which images exist for this vehicle (01.jpg to 05.jpg and 360.jpeg)
  useEffect(() => {
    let isMounted = true; // Prevent duplicate API calls

    const checkImages = async () => {
      if (!isMounted) return;

      const images: string[] = [];

      // Check 360 panorama image (try both .jpg and .jpeg)
      let panoramaPath = `/${currentVehicle.id}/360.jpg`;
      try {
        let response = await fetch(panoramaPath, { method: 'HEAD' });
        if (response.ok && isMounted) {
          setHas360Image(true);
          setImage360Path(panoramaPath);
        } else {
          // Try .jpeg extension
          panoramaPath = `/${currentVehicle.id}/360.jpeg`;
          response = await fetch(panoramaPath, { method: 'HEAD' });
          if (response.ok && isMounted) {
            setHas360Image(true);
            setImage360Path(panoramaPath);
          } else if (isMounted) {
            setHas360Image(false);
          }
        }
      } catch (error) {
        if (isMounted) {
          setHas360Image(false);
        }
      }

      // Check regular images (01.jpg to 05.jpg)
      for (let i = 1; i <= 5; i++) {
        if (!isMounted) break;

        const imagePath = `/${currentVehicle.id}/${i.toString().padStart(2, '0')}.jpg`;
        try {
          const response = await fetch(imagePath, { method: 'HEAD' });
          if (response.ok) {
            images.push(imagePath);
          }
        } catch {
          // Image doesn't exist, skip
        }
      }

      if (!isMounted) return;

      if (images.length === 0) {
        // Fallback to placeholder if no images found
        images.push(currentVehicle.image_url || '/placeholder-car.svg');
      }
      setAvailableImages(images);
    };

    if (currentVehicle) {
      checkImages();
    }

    return () => {
      isMounted = false;
    };
  }, [currentVehicle]);

  if (!currentVehicle || !startDateTime || !endDateTime) {
    navigate('/vehicles');
    return null;
  }

  const totalHours = getTotalHours();
  const exactDuration = getExactDuration();
  const days = Math.floor(exactDuration.hours / 24);
  const remainingHours = exactDuration.hours % 24;
  const remainingMinutes = exactDuration.minutes;

  const handleReserve = () => {
    setSelectedVehicle(currentVehicle);
    navigate('/checkout');
  };

  const handleBack = () => {
    navigate('/vehicles');
  };

  // SEO 메타 정보 생성
  const pageTitle = t('vehicleDetail.seoTitle', { vehicleName: getModelLabel(currentVehicle.model_name) });
  const pageDescription = t('vehicleDetail.seoDescription', {
    vehicleName: getModelLabel(currentVehicle.model_name),
    carClass: getCarClassLabel(currentVehicle.car_class) || t('vehicleDetail.vehicle'),
    capacity: currentVehicle.capacity || '5',
    fuel: getFuelLabel(currentVehicle.fuel_type || currentVehicle.fuel) || t('vehicleList.gasoline'),
  });
  const vehicleImage = availableImages[0] || `/car/${currentVehicle.id || 'default'}.png`;

  // Schema.org 구조화된 데이터 (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": getModelLabel(currentVehicle.model_name),
    "description": t('vehicleDetail.schemaDescription', { vehicleName: getModelLabel(currentVehicle.model_name) }),
    "brand": {
      "@type": "Brand",
      "name": t('brand.name')
    },
    "category": "Car Rental",
    "image": `https://goodtimerent.kr${vehicleImage}`,
    "offers": {
      "@type": "Offer",
      "price": currentVehicle.weekday_rate || 50000,
      "priceCurrency": "KRW",
      "availability": isVehicleAvailable ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": t('brand.name'),
        "telephone": "1600-4412",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": isEnglish ? "Busan" : "부산",
          "addressCountry": "KR"
        }
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": t('vehicleDetail.vehicleClass'),
        "value": getCarClassLabel(currentVehicle.car_class) || t('vehicleDetail.standard')
      },
      {
        "@type": "PropertyValue",
        "name": t('vehicleDetail.fuel'),
        "value": getFuelLabel(currentVehicle.fuel_type || currentVehicle.fuel) || t('vehicleList.gasoline')
      },
      {
        "@type": "PropertyValue",
        "name": t('vehicleDetail.capacity'),
        "value": `${currentVehicle.capacity || 5}${t('vehicleList.seats')}`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://goodtimerent.kr/vehicle-detail`} />
        <meta property="og:image" content={`https://goodtimerent.kr${vehicleImage}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`https://goodtimerent.kr${vehicleImage}`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-500 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between relative">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <div className="flex flex-col items-start">
                <span className="text-sm text-white/90 font-semibold">{t('vehicleDetail.vehicleList')}</span>
              </div>
            </button>
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-3">
              <img src="/logo.svg" alt={t('brand.name')} className="h-8" />
              <h1 className="text-xl font-semibold text-white">{t('vehicleDetail.title')}</h1>
            </div>
            <div className="w-20"></div>
          </div>

          {/* Mobile Header */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={handleBack}
              className="flex items-center text-white/90 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex items-center gap-2 flex-1">
              <img src="/logo.svg" alt={t('brand.name')} className="h-7" />
              <h1 className="text-lg font-semibold text-white truncate">{t('flow.vehicleDetail')}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Progress Indicator - Desktop Only */}
          <div className="mb-8 hidden md:block">
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    ✓
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.dateSelect')}</span>
                </div>
                <div className="w-16 h-1 bg-primary-600 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    2
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.vehicleSelect')}</span>
                </div>
                <div className="w-16 h-1 bg-gray-300 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-semibold">
                    3
                  </div>
                  <span className="ml-2 font-medium text-gray-500">{t('flow.reservationInfo')}</span>
                </div>
                <div className="w-16 h-1 bg-gray-300 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-semibold">
                    4
                  </div>
                  <span className="ml-2 font-medium text-gray-500">{t('flow.payment')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator - Mobile Simplified */}
          <div className="mb-4 md:hidden">
            <div className="flex items-center justify-center gap-1.5">
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  ✓
                </div>
                <span className="text-[10px] font-medium text-gray-700">{t('flow.date')}</span>
              </div>
              <div className="w-4 h-0.5 bg-primary-600"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  2
                </div>
                <span className="text-[10px] font-medium text-gray-900">{t('flow.vehicle')}</span>
              </div>
              <div className="w-4 h-0.5 bg-gray-300"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-[10px] font-bold">
                  3
                </div>
                <span className="text-[10px] font-medium text-gray-500">{t('flow.reservation')}</span>
              </div>
              <div className="w-4 h-0.5 bg-gray-300"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-[10px] font-bold">
                  4
                </div>
                <span className="text-[10px] font-medium text-gray-500">{t('flow.payment')}</span>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Content - Vehicle Details */}
          <div className="lg:col-span-2">
            {/* Vehicle Image Gallery */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              {availableImages.length > 0 && (
                <>
                  {/* Main Image */}
                  <div className="relative h-64 md:h-[600px] bg-gray-100">
                    {/* Loading Spinner */}
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <div className="flex flex-col items-center gap-3">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                          <p className="text-sm text-gray-500">{t('vehicleDetail.imageLoading')}</p>
                        </div>
                      </div>
                    )}
                    <img
                      src={availableImages[currentImageIndex]}
                      alt={`${getModelLabel(currentVehicle.model_name)} ${currentImageIndex + 1}`}
                      className={`w-full h-full object-cover transition-opacity duration-200 ${
                        imageLoading ? 'opacity-0' : 'opacity-100'
                      }`}
                      onLoad={() => setImageLoading(false)}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = '/placeholder-car.svg';
                        setImageLoading(false);
                      }}
                    />
                    {/* Navigation Arrows */}
                    {availableImages.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? availableImages.length - 1 : prev - 1))}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex((prev) => (prev === availableImages.length - 1 ? 0 : prev + 1))}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                    {/* Image Counter */}
                    {availableImages.length > 1 && (
                      <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                        {currentImageIndex + 1} / {availableImages.length}
                      </div>
                    )}
                  </div>
                  {/* Thumbnail Navigation */}
                  {availableImages.length > 1 && (
                    <div className="p-4 flex gap-2 overflow-x-auto">
                      {availableImages.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                            index === currentImageIndex ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          <img
                            src={image}
                            alt={t('vehicleDetail.thumbnailAlt', { index: index + 1 })}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Vehicle Reviews */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-bold mb-4 pb-4 border-b">{t('vehicleDetail.reviews')}</h3>
              <VehicleReviews vehicleName={currentVehicle.model_name} />
            </div>

            {/* 360 Panorama View */}
            {has360Image && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-bold mb-4 pb-4 border-b">{t('vehicleDetail.panorama')}</h3>
                <Vehicle360Viewer imagePath={image360Path} vehicleName={getModelLabel(currentVehicle.model_name)} />
              </div>
            )}

            {/* Vehicle Info */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-bold mb-4 pb-4 border-b">{t('vehicleDetail.vehicleInfo')}</h3>
              <h2 className="text-3xl font-bold mb-2">{getModelLabel(currentVehicle.model_name)}</h2>
              <p className="text-gray-600 mb-6">{getCarClassLabel(currentVehicle.car_class)}</p>

              {/* Specifications Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <div>
                    <div className="text-sm text-gray-600">{t('vehicleDetail.capacity')}</div>
                    <div className="font-semibold">{currentVehicle.seating_capacity}{t('vehicleList.seats')}</div>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <div>
                    <div className="text-sm text-gray-600">{t('vehicleDetail.fuel')}</div>
                    <div className="font-semibold">{getFuelLabel(currentVehicle.fuel_type)}</div>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <div className="text-sm text-gray-600">{t('vehicleDetail.available')}</div>
                    <div className="font-semibold text-green-600">{currentVehicle.count}{t('vehicleDetail.unit')}</div>
                  </div>
                </div>

                {(currentVehicle.license_plate || (currentVehicle as any).carnum) && (
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      <rect x="2" y="5" width="20" height="14" rx="2" strokeWidth={2} />
                    </svg>
                    <div>
                      <div className="text-sm text-gray-600">{t('vehicleDetail.licensePlate')}</div>
                      <div className="font-semibold">{currentVehicle.license_plate || (currentVehicle as any).carnum}</div>
                    </div>
                  </div>
                )}

                {(currentVehicle as any).company && (
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <div>
                      <div className="text-sm text-gray-600">{t('vehicleDetail.manufacturer')}</div>
                      <div className="font-semibold">{getCompanyLabel((currentVehicle as any).company)}</div>
                    </div>
                  </div>
                )}

                {(currentVehicle.year || ((currentVehicle as any).caryear && (currentVehicle as any).carmonth)) && (
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <div className="text-sm text-gray-600">{t('vehicleDetail.modelYear')}</div>
                      <div className="font-semibold">{formatYear()}</div>
                    </div>
                  </div>
                )}

                {/* Vehicle Features with icons */}
                {currentVehicle.features && currentVehicle.features.map((feature, idx) => {
                  // Parse "키: 값" format
                  const parts = feature.split(':').map(p => p.trim());
                  const label = parts.length > 1 ? parts[0] : '특징';
                  const rawValue = parts.length > 1 ? parts.slice(1).join(':').trim() : feature;

                  // 위치인 경우 태그 아이콘, 그 외는 별 아이콘
                  const isLocation = label === '위치';
                  const displayLabel = isLocation ? t('vehicleList.location') : t('vehicleList.feature');
                  const value = isLocation ? getLocationLabel(rawValue) : rawValue;

                  return (
                    <div key={idx} className="flex items-start">
                      {isLocation ? (
                        <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-blue-600 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      )}
                      <div>
                        <div className="text-sm text-gray-600">{displayLabel}</div>
                        <div className="font-semibold">{value}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Included Options */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-bold mb-4 pb-4 border-b">{t('vehicleDetail.includedOptions')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {(() => {
                  // Get options from CarSomoName field
                  const carSomoName = (currentVehicle as any).CarSomoName || (currentVehicle as any).carsomoname || '';
                  const options = carSomoName
                    ? carSomoName.split(',').map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0)
                    : [];

                  // Icon mapping function
                  const getIcon = (optionName: string) => {
                    const name = optionName.toLowerCase();

                    if (name.includes('네비게이션') || name.includes('네비')) {
                      return (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      );
                    } else if (name.includes('블랙박스')) {
                      return (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      );
                    } else if (name.includes('하이패스')) {
                      return (
                        <>
                          {/* 도로 경계선 */}
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 2L6 22M14 2L18 22" />
                          {/* 중앙 차선 (점선) */}
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} strokeDasharray="2,4" d="M12 2v20" />
                        </>
                      );
                    } else if (name.includes('블루투스')) {
                      return (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                      );
                    } else if (name.includes('usb')) {
                      return (
                        <>
                          <rect x="7" y="10" width="10" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10V6h6v4M10.5 14v3M13.5 14v3" />
                        </>
                      );
                    } else if (name.includes('열선') || name.includes('시트')) {
                      return (
                        <>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 4c0 2 1 3 0 4s-1 2 0 4 1 3 0 4-1 2 0 4" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4c0 2 1 3 0 4s-1 2 0 4 1 3 0 4-1 2 0 4" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 4c0 2 1 3 0 4s-1 2 0 4 1 3 0 4-1 2 0 4" />
                        </>
                      );
                    } else if (name.includes('무선') && name.includes('충전')) {
                      return (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      );
                    } else if (name.includes('메트') || name.includes('매트')) {
                      return (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                      );
                    } else {
                      // Default icon
                      return (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      );
                    }
                  };

                  if (options.length === 0) {
                    return (
                      <div className="col-span-2 md:col-span-3 text-center text-gray-500 py-4">
                        {t('vehicleDetail.noOptions')}
                      </div>
                    );
                  }

                  return options.map((option: string, index: number) => (
                    <div key={index} className="flex items-center text-gray-700">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {getIcon(option)}
                        </svg>
                      </div>
                      {getOptionLabel(option)}
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Insurance guide */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4 pb-4 border-b">{t('vehicleDetail.insuranceGuide')}</h3>

              <div className="space-y-4">
                {/* 일반자차 */}
                <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-base font-bold text-blue-900 mb-2">{t('vehicleDetail.standardInsurance')}</h4>
                      <p className="text-sm text-blue-800">{t('vehicleDetail.standardInsuranceDesc')}</p>
                      <p className="text-xs text-blue-700 mt-1">{t('vehicleDetail.standardInsuranceNote')}</p>
                    </div>
                  </div>
                </div>

                {/* 미가입 */}
                <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-base font-bold text-red-900 mb-2">{t('vehicleDetail.notIncluded')}</h4>
                      <p className="text-sm text-red-800">{t('vehicleDetail.notIncludedDesc')}</p>
                      <p className="text-xs text-red-700 mt-2">{t('vehicleDetail.notIncludedNote')}</p>
                    </div>
                  </div>
                </div>

                {/* 꼭 확인하세요 */}
                <div className="pt-4">
                  <h4 className="text-base font-bold text-gray-900 mb-2">{t('vehicleDetail.mustCheck')}</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-2">•</span>
                      <span>{t('vehicleDetail.insuranceOneAccident')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-500 mr-2">•</span>
                      <span className="font-semibold">{t('vehicleDetail.exclusionsTitle')}</span>
                    </li>
                    <li className="ml-4 text-xs text-gray-600">
                      {t('vehicleDetail.exclusions')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Booking Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 md:sticky md:top-6">
              <h3 className="text-lg font-bold mb-4 pb-4 border-b">{t('vehicleDetail.bookingSummary')}</h3>

              {/* Rental Period */}
              <div className="mb-6 relative">
                {/* Date Card - Clickable */}
                <div
                  onClick={() => setShowDateModal(true)}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-4 cursor-pointer hover:shadow-md transition-all border-2 border-gray-200 hover:border-gray-300"
                >
                  <div className="space-y-3">
                    {/* Start Date */}
                    <div>
                      <div className="flex items-center justify-center gap-4 mb-1">
                        <span className="text-sm text-gray-400 font-semibold">{t('vehicleList.rentalDate')}</span>
                        <div className="flex items-baseline gap-0.5">
                          <span className="text-2xl font-bold text-gray-900">
                            {startDateTime.getMonth() + 1}/{startDateTime.getDate()}
                          </span>
                          <span className="text-base font-medium text-gray-700">
                            ({weekdays[startDateTime.getDay()]})
                          </span>
                          <span className="text-xl font-bold text-gray-900 ml-2">
                            {startDateTime.getHours().toString().padStart(2, '0')}:{startDateTime.getMinutes().toString().padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="flex items-center justify-center py-1">
                      <div className="bg-blue-600 text-white px-4 py-1.5 rounded-full">
                        <span className="text-sm font-bold whitespace-nowrap">
                          {formatDuration()}
                        </span>
                      </div>
                    </div>

                    {/* End Date */}
                    <div>
                      <div className="flex items-center justify-center gap-4 mb-1">
                        <span className="text-sm text-gray-400 font-semibold">{t('vehicleList.returnDate')}</span>
                        <div className="flex items-baseline gap-0.5">
                          <span className="text-2xl font-bold text-gray-900">
                            {endDateTime.getMonth() + 1}/{endDateTime.getDate()}
                          </span>
                          <span className="text-base font-medium text-gray-700">
                            ({weekdays[endDateTime.getDay()]})
                          </span>
                          <span className="text-xl font-bold text-gray-900 ml-2">
                            {endDateTime.getHours().toString().padStart(2, '0')}:{endDateTime.getMinutes().toString().padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Edit hint */}
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-center text-xs text-gray-600">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {t('vehicleList.changeDate')}
                  </div>
                </div>

                {/* Date Time Selector Modal - positioned below the card */}
                {showDateModal && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 bg-black bg-opacity-30 z-[9999]"
                      onClick={() => setShowDateModal(false)}
                    />

                    {/* Popup */}
                    <div className="absolute top-full left-0 right-0 -mt-12 z-[10000]">
                      <div className="bg-white rounded-lg shadow-2xl">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-2 rounded-t-lg flex items-center justify-between">
                          <h3 className="font-bold text-sm">{t('vehicleList.dateTimeSelect')}</h3>
                          <button
                            onClick={() => setShowDateModal(false)}
                            className="p-0.5 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        {/* Content */}
                        <div className="p-2">
                          {/* Calendar */}
                          <div className="mb-2">
                            <RangeCalendar
                              startDate={tempStartDate}
                              endDate={tempEndDate}
                              onSelectRange={handleRangeSelect}
                            />
                          </div>

                          {/* Time Selection */}
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            {/* Start Time */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('vehicleList.rentalStart')}</label>
                              <select
                                value={tempStartTime}
                                onChange={(e) => {
                                  const newStartTime = e.target.value;
                                  setTempStartTime(newStartTime);

                                  // Auto-adjust end time if less than 5 hours
                                  if (tempStartDate && tempEndDate) {
                                    const [startHour, startMin] = newStartTime.split(':').map(Number);
                                    const [endHour, endMin] = tempEndTime.split(':').map(Number);

                                    const isSameDay = tempStartDate.toDateString() === tempEndDate.toDateString();

                                    if (isSameDay) {
                                      const startMinutes = startHour * 60 + startMin;
                                      const endMinutes = endHour * 60 + endMin;
                                      const diff = endMinutes - startMinutes;

                                      // If less than 5 hours, adjust end time
                                      if (diff < 5 * 60) {
                                        const newEndMinutes = startMinutes + 5 * 60;
                                        const newEndHour = Math.floor(newEndMinutes / 60);
                                        const newEndMin = newEndMinutes % 60;

                                        // If end time exceeds 22:00, move to next day
                                        if (newEndHour > 22 || (newEndHour === 22 && newEndMin > 0)) {
                                          const nextDay = new Date(tempEndDate);
                                          nextDay.setDate(nextDay.getDate() + 1);
                                          setTempEndDate(nextDay);
                                          setTempEndTime('08:00');
                                        } else {
                                          setTempEndTime(`${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`);
                                        }
                                      }
                                    }
                                  }
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {Array.from({ length: 29 }, (_, i) => {
                                  const totalMinutes = 8 * 60 + i * 30; // Start from 8:00, increment by 30 minutes
                                  const hour = Math.floor(totalMinutes / 60);
                                  const minute = totalMinutes % 60;
                                  if (hour > 22) return null; // Stop at 22:00

                                  // Check if this time is in the past for today
                                  const now = new Date();
                                  const isToday = tempStartDate && tempStartDate.toDateString() === now.toDateString();
                                  let disabled = false;
                                  if (isToday) {
                                    const currentHour = now.getHours();
                                    const currentMinute = now.getMinutes();
                                    const currentTotalMinutes = currentHour * 60 + currentMinute;
                                    disabled = totalMinutes < currentTotalMinutes;
                                  }

                                  const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                                  return (
                                    <option key={timeString} value={timeString} disabled={disabled}>
                                      {timeString}
                                    </option>
                                  );
                                }).filter(Boolean)}
                              </select>
                            </div>

                            {/* End Time */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('vehicleList.returnTime')}</label>
                              <select
                                value={tempEndTime}
                                onChange={(e) => setTempEndTime(e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {Array.from({ length: 29 }, (_, i) => {
                                  const totalMinutes = 8 * 60 + i * 30; // Start from 8:00, increment by 30 minutes
                                  const hour = Math.floor(totalMinutes / 60);
                                  const minute = totalMinutes % 60;
                                  if (hour > 22) return null; // Stop at 22:00

                                  // Calculate minimum end time (start time + 5 hours)
                                  const [startHour, startMin] = tempStartTime.split(':').map(Number);
                                  const startTotalMinutes = startHour * 60 + startMin;

                                  // Check if same day or different day
                                  const isSameDay = tempStartDate && tempEndDate &&
                                    tempStartDate.toDateString() === tempEndDate.toDateString();

                                  let disabled = false;
                                  if (isSameDay) {
                                    // Minimum 5 hours from start time
                                    const minEndMinutes = startTotalMinutes + 5 * 60;
                                    disabled = totalMinutes < minEndMinutes;
                                  }

                                  const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                                  return (
                                    <option key={timeString} value={timeString} disabled={disabled}>
                                      {timeString}
                                    </option>
                                  );
                                }).filter(Boolean)}
                              </select>
                            </div>
                          </div>

                          {/* Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowDateModal(false)}
                              className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded transition-colors text-xs font-medium"
                            >
                              {t('common.cancel')}
                            </button>
                            <button
                              onClick={handleSaveDateTimeModal}
                              disabled={!tempStartDate || !tempEndDate || checkingAvailability}
                              className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-xs font-medium"
                            >
                              {checkingAvailability ? t('vehicleDetail.checking') : t('common.apply')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Total Amount */}
              <div className="mb-6 pb-6 border-b">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">{t('vehicleDetail.totalPrice')}</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {(() => {
                      // 대여 기간 동안의 총 요금 계산
                      if (!startDateTime || !endDateTime) return formatPrice(0);

                      const totalHours = Math.ceil((endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60));

                      // 최소 5시간부터 예약 가능
                      if (totalHours < 5) return formatPrice(0);

                      const weekdayRate = currentVehicle.weekday_rate || 50000;
                      const weekendRate = currentVehicle.weekend_rate || 70000;
                      const fiveHourRate = currentVehicle.five_hour_rate || 0;

                      // 주말 판별 함수 (금,토,일)
                      const isWeekend = (date: Date) => {
                        const day = date.getDay();
                        return day === 0 || day === 5 || day === 6;
                      };

                      const startWeekend = isWeekend(startDateTime);
                      const baseRate = startWeekend ? weekendRate : weekdayRate;

                      let totalAmount = 0;

                      // 48시간 주기로 계산
                      const full48hCycles = Math.floor(totalHours / 48);
                      const remainingHours = totalHours % 48;

                      // 완전한 48시간 사이클 요금 추가
                      totalAmount += full48hCycles * baseRate * 2;

                      // 남은 시간에 대한 요금 계산 (0~47시간)
                      if (remainingHours > 0) {
                        if (remainingHours >= 1 && remainingHours <= 4) {
                          totalAmount += baseRate * remainingHours * 0.1;
                        } else if (remainingHours === 5) {
                          totalAmount += fiveHourRate > 0 ? fiveHourRate : baseRate * 0.4;
                        } else if (remainingHours >= 6 && remainingHours <= 18) {
                          totalAmount += baseRate * 0.9;
                        } else if (remainingHours >= 19 && remainingHours <= 24) {
                          totalAmount += baseRate;
                        } else if (remainingHours >= 25 && remainingHours <= 33) {
                          const additionalHours = remainingHours - 24;
                          totalAmount += baseRate * (1 + additionalHours * 0.1);
                        } else if (remainingHours >= 34 && remainingHours <= 36) {
                          totalAmount += baseRate * 2 * 0.95;
                        } else if (remainingHours >= 37 && remainingHours <= 47) {
                          totalAmount += baseRate * 2;
                        }
                      }

                      return formatPrice(Math.round(totalAmount));
                    })()}
                  </span>
                </div>
                <div className="text-xs text-gray-500">{t('vehicleDetail.vatIncluded')}</div>
              </div>

              {/* Availability Warning */}
              {!isVehicleAvailable && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-red-800">{t('vehicleDetail.unavailableTitle')}</p>
                      <p className="text-xs text-red-600 mt-1">{t('vehicleDetail.unavailableDesc')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons - Desktop */}
              <div className="hidden md:block space-y-3">
                <button
                  onClick={handleReserve}
                  disabled={!isVehicleAvailable}
                  className={`w-full px-6 py-4 rounded-lg transition-colors font-semibold text-lg flex items-center justify-center ${
                    isVehicleAvailable
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('vehicleDetail.reserveNow')}
                </button>
                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {t('vehicleDetail.contactReservation')}
                </button>
              </div>

              {/* Contact Modal */}
              {showContactModal && (
                <>
                  <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setShowContactModal(false)}
                  />
                  <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">{t('vehicleDetail.contactReservation')}</h3>
                        <button
                          onClick={() => setShowContactModal(false)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="flex items-center mb-3">
                            <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span className="font-semibold text-gray-900">{t('vehicleDetail.phoneInquiry')}</span>
                          </div>
                          <a href="tel:1600-4412" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                            1600-4412
                          </a>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p>{t('vehicleDetail.businessHours')}</p>
                          <p>{t('vehicleDetail.weekendConsult')}</p>
                        </div>
                        <button
                          onClick={() => setShowContactModal(false)}
                          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold"
                        >
                          {t('common.confirm')}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Other Vehicle Button */}
              <div className="mt-4">
                <button
                  onClick={handleBack}
                  className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-lg transition-colors font-medium"
                >
                  {t('vehicleDetail.viewOtherVehicles')}
                </button>
              </div>

              {/* Rental Terms */}
              <div className="mt-6 pt-6 border-t">
                <div className="text-sm text-gray-600 mb-2">{t('vehicleDetail.rentalTerms')}</div>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>{t('vehicleDetail.termAge')}</li>
                  <li>{t('vehicleDetail.termLicense')}</li>
                </ul>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Buttons */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-30">
        <div className="flex gap-2">
          <button
            onClick={() => setShowContactModal(true)}
            className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center font-medium"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {t('vehicleDetail.inquiryShort')}
          </button>
          <button
            onClick={handleReserve}
            disabled={!isVehicleAvailable}
            className={`flex-[2] px-4 py-3 rounded-lg transition-colors font-bold flex items-center justify-center ${
              isVehicleAvailable
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('vehicleDetail.reserveNow')}
          </button>
        </div>
      </div>

      {/* Add padding at bottom for mobile to prevent content from being hidden behind fixed buttons */}
      <div className="md:hidden h-20"></div>
      </div>
    </>
  );
};

export default VehicleDetailPage;
